<template>
  <div
    class="shrink-0 relative z-0 hover:z-30"
    :class="fill ? 'w-full h-full' : 'rounded-lg w-9 h-9'"
  >
    <apexchart
      class="mini-portfolio-chart"
      type="treemap"
      :width="fill ? '100%' : 36"
      :height="fill ? '100%' : 36"
      :options="chartOptions"
      :series="series"
    />
  </div>
</template>

<script setup>
import VueApexCharts from 'vue3-apexcharts'
import { computed } from 'vue'
import { PORTFOLIO_PALETTE, NEGATIVE_RETURN_PALETTE } from '../lib/apexTheme.js'
import { renderPortfolioTooltipTable, toWeightedReturn } from './portfolioTooltipTable.js'

const apexchart = VueApexCharts

const NEUTRAL_HEAT = '#64748b'

const props = defineProps({
  stocks: {
    type: Array, // [{ symbol: string, weight: number, return_pct: number }]
    required: true,
  },
  /** Fill available parent square (used in Home leaderboard rows). */
  fill: {
    type: Boolean,
    default: false,
  },
  /**
   * Show labels inside treemap blocks.
   * Large blocks: symbol + weight, medium: symbol only, tiny: hidden.
   */
  showLabels: {
    type: Boolean,
    default: false,
  },
})

const colorForRank = (palette, rank, reverse = false) => {
  const idx = Math.min(rank, palette.length - 1)
  return reverse ? palette[palette.length - 1 - idx] : palette[idx]
}

const series = computed(() => [{
  data: (() => {
    const points = props.stocks.map((stock, index) => ({
      index,
      x: stock.symbol,
      y: stock.weight,
      returnPct: stock.return_pct ?? 0,
      weightedReturn: toWeightedReturn(stock),
      fillColor: NEUTRAL_HEAT,
    }))

    const positives = points
      .filter((p) => p.weightedReturn > 0)
      .sort((a, b) => b.weightedReturn - a.weightedReturn)
    positives.forEach((point, rank) => {
      point.fillColor = colorForRank(PORTFOLIO_PALETTE, rank)
    })

    const negatives = points
      .filter((p) => p.weightedReturn < 0)
      .sort((a, b) => b.weightedReturn - a.weightedReturn)
    negatives.forEach((point, rank) => {
      point.fillColor = colorForRank(NEGATIVE_RETURN_PALETTE, rank, true)
    })

    return points.sort((a, b) => a.index - b.index).map(({ index, ...point }) => point)
  })(),
}])

const chartOptions = computed(() => ({
  chart: {
    type: 'treemap',
    sparkline: { enabled: true },
    events: {
      dataPointSelection: (event) => {
        event?.preventDefault?.()
        return false
      },
    },
  },
  stroke: {
    width: 2,
    colors: ['transparent'],
  },
  plotOptions: {
    treemap: {
      distributed: false,
      enableShades: false,
      borderRadius: 0,
    },
  },
  dataLabels: {
    enabled: props.showLabels,
    formatter: (_, { seriesIndex, dataPointIndex, w }) => {
      if (!props.showLabels) return ''
      const point = w.config.series[seriesIndex]?.data?.[dataPointIndex]
      if (!point) return ''

      const weight = Number(point.y ?? 0)
      if (weight < 5) return ''
      if (weight < 10) return `${point.x}`
      return `${point.x}\n${Math.round(weight)}%`
    },
    style: {
      fontSize: '11px',
      fontWeight: 700,
      colors: ['#ffffff'],
    },
    offsetY: 0,
  },
  tooltip: {
    enabled: true,
    shared: false,
    intersect: true,
    custom: ({ dataPointIndex, w }) => {
      const point = w.config.series[0].data[dataPointIndex]
      return renderPortfolioTooltipTable({
        symbol: point.x,
        weightPct: point.y ?? 0,
        returnPct: point.returnPct ?? 0,
        weightedReturnPct: point.weightedReturn ?? 0,
      })
    },
  },
}))
</script>

<style scoped>
:deep(.mini-portfolio-chart .apexcharts-canvas) {
  overflow: visible !important;
}

:deep(.mini-portfolio-chart .apexcharts-tooltip) {
  z-index: 40 !important;
}

:deep(.mini-portfolio-chart .apexcharts-svg) {
  border-radius: 0.5rem;
}
</style>
